// components/RotaBuilder/PatternDialog.tsx - CLEANED UP VERSION
import React from 'react';
import {
  Dialog,
  DialogContent,
  Button,
  Typography,
  Box,
  Chip,
  IconButton
} from '@mui/material';
import { X, Check, Calendar, Clock } from 'lucide-react';
import { getRotaOptionById } from '../../constants/rotaOptions';

interface PatternDialogProps {
  open: boolean;
  optionId: string | null;
  dayName: string;
  monthName: string;
  affectedDates: string[];
  existingDates: string[];
  onConfirm: (applyToAll: boolean, skipExisting: boolean) => void;
  onCancel: () => void;
}

const PatternDialog: React.FC<PatternDialogProps> = ({
  open,
  optionId,
  dayName,
  monthName,
  affectedDates,
  existingDates,
  onConfirm,
  onCancel
}) => {
  const option = optionId ? getRotaOptionById(optionId) : null;

  if (!option) return null;

  const hasExistingEntries = existingDates.length > 0;
  const newDatesCount = affectedDates.length - existingDates.length;

  return (
    <Dialog 
      open={open} 
      onClose={onCancel}
      maxWidth="sm" 
      fullWidth
      PaperProps={{
        sx: { borderRadius: 3 }
      }}
    >
      {/* Header */}
      <Box sx={{ 
        p: 3, 
        pb: 2, 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'flex-start'
      }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>
            Create Pattern?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            You're setting <strong>{option.label}</strong> for <strong>{dayName}</strong>
          </Typography>
        </Box>
        <IconButton onClick={onCancel} size="small">
          <X size={20} />
        </IconButton>
      </Box>

      <DialogContent sx={{ pt: 0, pb: 3 }}>
        {/* Main Question */}
        <Box sx={{ 
          textAlign: 'center', 
          p: 3, 
          bgcolor: 'primary.50', 
          borderRadius: 2, 
          mb: 3,
          border: '2px solid',
          borderColor: 'primary.200'
        }}>
          <Calendar size={32} color="#1976d2" style={{ marginBottom: 12 }} />
          <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 600 }}>
            Apply to every {dayName} in {monthName}?
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Chip 
              size="small" 
              label={`${affectedDates.length} ${dayName}s`}
              color="primary"
              variant="outlined"
            />
            {newDatesCount > 0 && (
              <Chip 
                size="small" 
                label={`${newDatesCount} new entries`}
                color="success"
                variant="outlined"
              />
            )}
            {hasExistingEntries && (
              <Chip 
                size="small" 
                label={`${existingDates.length} existing`}
                color="warning"
                variant="outlined"
              />
            )}
          </Box>
        </Box>

        {/* Quick Info */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1, 
          p: 2, 
          bgcolor: 'grey.50', 
          borderRadius: 1, 
          mb: 3 
        }}>
          <Clock size={16} />
          <Typography variant="body2" color="text.secondary">
            Changes applied locally - click "Save Roster" when done
          </Typography>
        </Box>

        {/* Dates */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
            Dates: {affectedDates.map(date => new Date(date).getDate()).join(', ')}
          </Typography>
        </Box>

        {/* Actions */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Primary Action */}
          <Button
            onClick={() => onConfirm(true, hasExistingEntries)}
            variant="contained"
            size="large"
            startIcon={<Check size={18} />}
            sx={{ py: 1.5, fontSize: '1rem', fontWeight: 600 }}
          >
            Apply Pattern ({affectedDates.length} {dayName}s)
          </Button>
          
          {/* Secondary Actions */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              onClick={() => onConfirm(false, false)}
              variant="outlined"
              size="medium"
              sx={{ 
                flex: 1,
                py: 1,
                borderWidth: 2,
                '&:hover': { borderWidth: 2 }
              }}
            >
              Just This Day
            </Button>
            <Button
              onClick={onCancel}
              variant="text"
              size="medium"
              sx={{ px: 3 }}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default PatternDialog;